<template>
  <b-card no-body class="mb-2">
    <b-card-header class="pb-50">
      <b-row class="w-100">
        <b-col cols="12" md="9">
          <h4>Resumo anual Futuro Hub</h4>
        </b-col>
        <b-col cols="12" md="1">
          <b-form-group label="Ano" label-for="expenseSearchYear">
            <v-select
              id="expenseSearchYear"
              :value="year"
              :reduce="(option) => option"
              :options="years"
              :clearable="false"
              :disabled="expenseSearchVisualization == expensesVisualization.YEAR"
              @input="updateYear"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="Tipo de visualização" label-for="expenseVisualization">
            <v-select
              id="expenseVisualization"
              :value="expenseSearchVisualization"
              :reduce="(option) => option.value"
              :options="expensesVisualization.visualizationOptions"
              :clearable="false"
              label="label"
              @input="updateVisualization"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card-header>
    <div class="text-center" v-if="loading">
      <b-spinner class="mt-2" variant="secondary" large />
    </div>
    <div v-else class="m-2">
      <b-table 
        striped 
        bordered 
        hover 
        :items="tableItems" 
        :fields="tableFields"
        responsive
        class="incomes-table"
      >
      </b-table>
    </div>
    <div class="chart-container" >
      <vue-apex-charts
        v-if="series.length"
        type="line"
        width="100%"
        height="300"
        :options="chartOptions"
        :series="series"
      ></vue-apex-charts>
    </div>
  </b-card>
</template>
  
<script>
import _ from 'lodash'
import VueApexCharts from "vue-apexcharts";
import { mapGetters, mapActions, mapMutations } from "vuex";
import * as types from "../store/types";
import { useToast } from "vue-toastification/composition";
import useAppConfig from "@core/app-config/useAppConfig";
import { toCurrency } from "@/helpers/converters.js";
import vSelect from "vue-select";
import * as expensesVisualization from '@/constants/expenses_visualizations'; 
  
export default {
  components: {
    VueApexCharts,
    vSelect
  },
  setup() {
    const { skin } = useAppConfig();
    return { toast: useToast(), skin };
  },
  data() {
    return {
      expensesVisualization,
      primaryColor: "#48da89",
      defaultColor: "#b4b7bd",
      loading: true,
    };
  },
  computed: {
    ...mapGetters({
      yearIncomeChartData: types.YEAR_INCOME_CHART_DATA,
      expenseSearchYear: types.EXPENSES_SEARCH_YEAR,
      expenseSearchVisualization: types.EXPENSES_SEARCH_VISUALIZATION,
    }),
    year() {
      return this.expenseSearchYear;
    },
    series() {
      const series = this.yearIncomeChartData?.series || [];
      if(this.expenseSearchVisualization == expensesVisualization.QUARTER){
        return _.map(series, (item) => {
          const chunks = _.chunk(item.data, 3)
          const values = _.map(chunks, (value) => _.sum(value))
          return { ...item, data: values }
        })
      } 
      return series;
    },
    categories() {
      const categories = this.yearIncomeChartData?.categories || [];
      if(this.expenseSearchVisualization == expensesVisualization.QUARTER){
        return _.chunk(categories, 3).map((e, index) => {
          return `${index + 1}º Trimestre/${this.year}`
        })
      } 
      return categories;
    },
    tableItems() {
      const series = this.yearIncomeChartData?.series || [];
      const categories = this.yearIncomeChartData?.categories || [];
      const lines = _.map(series, (item) => {
        var months = []
        if(this.expenseSearchVisualization == expensesVisualization.QUARTER){
          const chunks = _.chunk(item.data, 3)
          const values = _.map(chunks, (value) => toCurrency(_.sum(value), 0, 2, true))
          months = _.map(categories, (e, index) => {
            return {  [`${index + 1}º Trimestre/${this.year}`]: values[index] }
          })
        } else {
          const values = _.map(item.data, (value) => toCurrency(value, 0, 2, true))
          months = _.map(categories, (e, index) => {
            return { [e]: values[index] }
          })
        }
        const total = _.sum(item.data)
        return _.merge( { desc: item.name }, ...months, { total: toCurrency(total, 0, 2, true)} )
      })
      return lines
    },
    tableFields() {
      var months = _.map(this.categories, (e) => {
        e = e.toString()
        return { key: e, label: e, sortable: false, trClass: 'p-0' }
      })
      return [
        { key: 'desc', label: 'Meses', sortable: false },
        ...months,
        { key: 'total', label: 'Total', sortable: false }
      ]
    },
    chartOptions() {
      return {
        chart: {
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          foreColor: this.skin === "dark" ? "#fff" : "",
        },
        legend: {
          position: 'top'
        },
        markers: {
          strokeWidth: 7,
          strokeOpacity: 1,
          colors: ["#247BA0", "#FF1654", "#FFC107", "#28C76F"],
        },
        colors: ["#247BA0", "#FF1654", "#FFC107", "#28C76F"],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'straight',
        },
        grid: {
          xaxis: {
            lines: {
              show: true,
            },
          },
        },
        tooltip: {
          custom(data) {
            return `${'<div class="px-1 py-50"><span>'}${
              toCurrency(data.series[data.seriesIndex][data.dataPointIndex])
            }</span></div>`
          },
          followCursor: true
        },
        xaxis: {
          categories:this.categories,
        },
        yaxis: {
          labels: {
            formatter: function (value) {
              return toCurrency(value) ;
            }
          },
        },
      }
    },
    years() {
      const currentYear = new Date().getFullYear();
      const startYear = Math.floor(currentYear / 10) * 10;
      return Array.from({ length: 10 }, (_, i) => startYear + i);
    },
  },
  watch: {
    totalExpensesByCostCenter(newVal) {
      if (newVal.length) {
        this.loading = false;
      }
    },
  },
  methods: {
    ...mapActions({
      getYearIncomeChartData: types.GET_YEAR_INCOME_CHART_DATA
    }),
    ...mapMutations({
      mutateExpenseSearchYear: types.MUTATE_EXPENSES_SEARCH_YEAR,
      mutateExpenseSearchVisualization: types.MUTATE_EXPENSES_SEARCH_VISUALIZATION,
    }),
    getData(){
      this.loading = true
      this.getYearIncomeChartData(this.year)
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os dados de receita. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateYear(year) {
      if(year){
        this.mutateExpenseSearchVisualization(expensesVisualization.MONTHLY);
      } 
      this.mutateExpenseSearchYear(year);
    },
    updateVisualization(visualization) {
      if(visualization == expensesVisualization.YEAR){
        this.mutateExpenseSearchYear(undefined)
      }else if(!this.year){
        this.mutateExpenseSearchYear(new Date().getFullYear());
      }
      this.mutateExpenseSearchVisualization(visualization);
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-container {
  width: 80%;
  height: 400px;
  margin: auto;
  padding: 20px;
}
</style>
<style lang="scss">
.incomes-table td{
  padding: 0.65rem !important;
}
</style>

